import { useEffect, useRef } from 'react'
import { useNewsSearch } from './useNewsSearch'
import { useNewsStore } from '../stores/newsStore'

/**
 * Hook for orchestrating search operations
 * Handles search execution, first-search tracking, and active keyword management
 */
export function useSearchOrchestration() {
  const { mutate: searchNews, isPending } = useNewsSearch()
  const {
    settings,
    activeKeyword,
    hasPerformedFirstSearch,
    setActiveKeyword,
    markFirstSearchComplete,
  } = useNewsStore()

  const handleSearch = (
    keywords: string[],
    options?: {
      display?: number
      sort?: 'sim' | 'date'
      shouldMarkFirstSearch?: boolean
      shouldSetActiveKeyword?: boolean
    }
  ) => {
    const {
      display = settings.display,
      sort = settings.sort,
      shouldMarkFirstSearch = true,
      shouldSetActiveKeyword = true,
    } = options || {}

    // Perform search
    searchNews({
      keywords,
      display,
      sort,
    })

    // Mark first search as complete
    if (shouldMarkFirstSearch && !hasPerformedFirstSearch) {
      markFirstSearchComplete()
    }

    // Set active keyword to first one if not set
    if (shouldSetActiveKeyword && keywords.length > 0 && !activeKeyword) {
      setActiveKeyword(keywords[0])
    }
  }

  return { handleSearch, isPending }
}

/**
 * Hook for auto-searching on mount, once per session.
 *
 * Gated on `tabs` rather than `keywords`: keywords are now persisted, so a
 * returning user arrives with keywords already set but no results, and a
 * keyword-based gate would leave them staring at empty tabs.
 */
export function useAutoSearch(registeredKeywords: string[]) {
  const { handleSearch, isPending } = useSearchOrchestration()
  const { tabs } = useNewsStore()
  const hasRun = useRef(false)

  useEffect(() => {
    if (hasRun.current || isPending) return
    if (registeredKeywords.length === 0) return
    if (Object.keys(tabs).length > 0) return

    hasRun.current = true
    handleSearch(registeredKeywords, {
      shouldMarkFirstSearch: false,
      shouldSetActiveKeyword: true,
    })
  }, [registeredKeywords, tabs, isPending, handleSearch])
}
